// 대화 한 번 — 스트림을 읽어 답을 이어 붙이고, 이용권 잔액을 함께 들고 있는다. 화면은 이 훅만 안다
//
// 잔액은 서버가 정한다. 대화가 끝날 때마다(성공·실패·환원 모두) 서버에서 다시 읽는다 — 화면이 장수를 직접 빼지 않는다.
// 이용권이 0장이면 서버가 402 를 돌려주고, 그때 error 가 'credits_exhausted' 가 된다
import { useCallback, useEffect, useRef, useState } from 'react';

import { sessionStore, useSession } from '../../../composition/plugin-runtime';
import { fetchBalance } from '../adapters/credits-api';
import { streamChat } from '../adapters/chat-api';
import { ChatRequestError, IDLE, STREAMING, failed, interrupted, reduce, type ChatMessage, type ChatState } from '../domain/chat';

export interface LlmChatParams {
  /** MVP API 의 대화 중계 */
  readonly chatEndpoint: string;
  /** MVP API 의 이용권 잔액 조회 */
  readonly creditsEndpoint: string;
}

export function useLlmChat({ chatEndpoint, creditsEndpoint }: LlmChatParams) {
  const signedIn = useSession() !== null;
  const [chat, setChat] = useState<ChatState>(IDLE);
  const [balance, setBalance] = useState<number | null>(null);
  // 대화가 도는 동안 다시 보내면 이용권만 두 장 나간다
  const running = useRef(false);
  const abort = useRef<AbortController | null>(null);
  const alive = useRef(true);

  const refreshBalance = useCallback(async () => {
    const session = sessionStore.get();
    if (!session) return;
    const next = await fetchBalance(creditsEndpoint, session.token);
    if (alive.current && next !== null) setBalance(next);
  }, [creditsEndpoint]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      abort.current?.abort(); // 화면을 떠났다 — 플랫폼 호출도 끊긴다
    };
  }, []);

  useEffect(() => {
    if (signedIn) void refreshBalance();
    else setBalance(null);
  }, [signedIn, refreshBalance]);

  const send = useCallback(
    async (messages: readonly ChatMessage[]): Promise<void> => {
      // 보내는 시점의 토큰을 쓴다 — 오래 열어 둔 화면의 토큰이 새로 발급됐을 수 있다
      const session = sessionStore.get();
      if (!session) {
        setChat(failed('login_required'));
        return;
      }
      if (running.current) return;
      running.current = true;
      const controller = new AbortController();
      abort.current = controller;
      let state = STREAMING;
      setChat(state);
      try {
        for await (const event of streamChat(chatEndpoint, session.token, messages, controller.signal)) {
          state = reduce(state, event);
          setChat(state);
        }
        state = interrupted(state);
        setChat(state);
      } catch (error) {
        if (controller.signal.aborted) {
          // 사용자가 멈췄다 — 받은 만큼을 답으로 둔다
          setChat({ status: 'done', text: state.text, error: null });
        } else if (error instanceof ChatRequestError) {
          if (error.kind === 'credits_exhausted') setBalance(0);
          setChat(failed(error.kind));
        } else {
          setChat(failed('failed', state.text));
        }
      } finally {
        running.current = false;
        if (abort.current === controller) abort.current = null;
      }
      await refreshBalance();
    },
    [chatEndpoint, refreshBalance],
  );

  const cancel = useCallback(() => abort.current?.abort(), []);

  return { signedIn, chat, balance, send, cancel };
}
